import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, take, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IdentifierType } from '@nimbus-assistant/enums/identifier-type';
import { TeamContactInfo } from '@nimbus-assistant/models/team-info';
import { AppRoutes } from '@nimbus-assistant/enums/app-routes';
import { UrlProtocolMessage } from '@nimbus-assistant/models/web-view-messages/url-protocol-message';
import { CallerIdentifier } from '@nimbus-assistant/models/task-attributes.model';
import { ITask, TaskConversationState } from '@nimbus-assistant/models/task.model';
import { Modality } from '@nimbus-assistant/models/task-types.model';
import { isTransferDisabled } from '@nimbus-assistant/helpers/task-priority.helpers';
import { CallTransfer } from '@nimbus-assistant/models/call-transfer';
import { TransferType } from '@nimbus-assistant/enums/transfer-type';
import { IAssistantUserAutoCompleteValueModel } from '@nimbus-assistant/interfaces/assistant-user-autocomplete-value-model';
import { Utilities } from '@nimbus-assistant/utils/utilities';
import {
  UserAutoCompleteCallOptionType,
  UserAutoCompleteValueType,
} from '@nimbus-library/features/shared/services/user-search/models/user-auto-complete-value-type';
import { OutboundCallState } from '@nimbus-global/features/shared/models/api/outbound-call-state.model';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { isConsultationSessionInProgress } from '@nimbus-library/features/shared/models/participant-info/consultation-session.utils';
import { ConversationOriginType } from '@nimbus-library/features/shared/models/participant-info/i-combined-conversation.response';
import { compareLocale } from '@nimbus-library/features/localization/utils/active-locale';
import { ApiService } from './api/api.service';
import { SettingsService } from './settings.service';
import { LoggingService } from './logging.service';
import { ErrorService } from './error.service';
import { UserSelectableState, UserStateService } from './user-state.service';
import { AssistantTasksService } from './assistant-tasks.service';
import { localStorageKeys, LocalStorageService } from './local-storage.service';
import { AssistantHub } from './signalr/assistant.hub';
import { TaskTransferService } from './task-transfer.service';
import { ContactsService } from './contacts.service';
import { AuthService } from './auth.service';
import { ServicesService } from './services.service';
import { GraphApiService } from './graph-api.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class TaskOutboundService {
  private static readonly outboundBlockingStates: ReadonlyArray<TaskConversationState> = [
    TaskConversationState.PutOnHold,
    TaskConversationState.Transferring,
  ];

  private static readonly consultPrefix = 'consult';

  private clickToCallPrefixes: string[] = ['nimbus-call', 'tel', 'callto'];
  private isInRona = false;
  private isOutboundDisabled = true;
  private hasNoConnection = false;

  public services$ = new BehaviorSubject<TeamContactInfo[]>([]);
  public clickToCallTarget$ = new BehaviorSubject<string>(null);
  public isAvailableForOutboundCall$ = new BehaviorSubject<boolean>(false);

  /** Single source of truth for whether placing an outbound / call-on-behalf is disabled. */
  public readonly isOutboundDisabled$: Observable<boolean>;

  public constructor(
    private readonly apiService: ApiService,
    private readonly settingsService: SettingsService,
    private readonly router: Router,
    private readonly errorService: ErrorService,
    private readonly userStateService: UserStateService,
    private readonly tasksService: AssistantTasksService,
    private readonly localStorageService: LocalStorageService,
    private readonly loggingService: LoggingService,
    private readonly hub: AssistantHub,
    private readonly transferService: TaskTransferService,
    private readonly contactsService: ContactsService,
    private readonly authService: AuthService,
    private readonly servicesService: ServicesService,
    private readonly graphApiService: GraphApiService,
  ) {
    this.settingsService.settings$.pipe(untilDestroyed(this)).subscribe((settings) => {
      if (settings.outboundServices && settings.outboundServices.length > 0) {
        this.services$.next(settings.outboundServices?.sort((a, b) => compareLocale(a.name, b.name)));
      }
    });

    this.hub.availabilityState$
      .pipe(
        filter((a) => !!a),
        untilDestroyed(this),
      )
      .subscribe((availability) => {
        this.isAvailableForOutboundCall$.next(availability.isAvailableForOutboundCall ?? false);
      });

    this.userStateService.isInRona$.pipe(untilDestroyed(this)).subscribe((v) => (this.isInRona = v));

    const noConnection$ = this.userStateService.selectableState$.pipe(
      map((state) => state === UserSelectableState.noConnectionError),
      distinctUntilChanged(),
    );
    noConnection$.pipe(untilDestroyed(this)).subscribe((v) => (this.hasNoConnection = v));

    this.isOutboundDisabled$ = combineLatest({
      isAvailableForOutboundCall: this.isAvailableForOutboundCall$,
      consultationInProgress: this.tasksService.connectedTask$.pipe(
        map((task) => isConsultationSessionInProgress(task?.consultationSession?.currentState)),
      ),
      inOutboundBlockingState: this.tasksService.connectedTask$.pipe(
        map((task) => TaskOutboundService.outboundBlockingStates.includes(task?.conversationState)),
      ),
      noConnection: noConnection$,
    }).pipe(
      map(
        ({ isAvailableForOutboundCall, consultationInProgress, inOutboundBlockingState, noConnection }) =>
          !isAvailableForOutboundCall || consultationInProgress || inOutboundBlockingState || noConnection,
      ),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.isOutboundDisabled$.pipe(untilDestroyed(this)).subscribe((disabled) => (this.isOutboundDisabled = disabled));
  }

  public processCall(targetOption: IAssistantUserAutoCompleteValueModel, serviceId: string): Observable<void> {
    return this.startOutboundCall(targetOption, serviceId);
  }

  public startOutboundCall(targetOption: IAssistantUserAutoCompleteValueModel, serviceId: string): Observable<void> {
    this.localStorageService.saveData(localStorageKeys.lastUsedOutboundService, serviceId);

    const target: CallerIdentifier = {
      id: targetOption.value,
      type: targetOption.type === UserAutoCompleteValueType.GUID ? IdentifierType.Guid : IdentifierType.Pstn,
    };

    return this.apiService
      .startOutboundCall({
        teamId: serviceId,
        callerIdentifier: target,
      })
      .pipe(
        catchError((err) => {
          this.loggingService.Info(`CallAs failed: ${JSON.stringify(err)}`);
          return of(null);
        }),
      );
  }

  public processClickToCall(msg: UrlProtocolMessage): void {
    // Initial server availability can briefly be available when reconnecting in persistent RONA:
    // the first fetch does not gate on RONA, only the streamed hot path does.
    if (this.isInRona) {
      return;
    }

    // A consultation is not an outbound call: only the connection is checked here, the call by canConsult().
    if (this.isConsultOnConnectedCall(msg) ? this.hasNoConnection : this.isOutboundDisabled) {
      return;
    }

    const await_auth_timeout = 3000;
    const settingsReceived$ = this.settingsService.settings$.pipe(
      filter((x) => !!x && !x.isDefault),
      timeout(await_auth_timeout),
      catchError(() => {
        this.errorService.showError(TranslationKeys.WarningPleaseLogIn, TranslationKeys.WarningAndTryAgain);
        return of(null);
      }),
      take(1),
      untilDestroyed(this),
    );

    settingsReceived$.subscribe((settingsReceived) => {
      if (settingsReceived) {
        this.processProtocolMessage(msg);
      }
    });
  }

  public cleanup(): void {
    this.clickToCallTarget$.next(null);
  }

  private processProtocolMessage(msg: UrlProtocolMessage): void {
    let target = msg?.data?.toLowerCase()?.split(';')[0];

    if (target?.startsWith(`${TaskOutboundService.consultPrefix}:`)) {
      this.processConsult(target.substring(TaskOutboundService.consultPrefix.length + 1), msg?.data);
      return;
    }

    this.clickToCallPrefixes.forEach((pre) => {
      if (target?.startsWith(pre)) {
        target = target.replace(`${pre}:`, '');
        this.loggingService.Info(`Received click-to-call protocol '${pre}' with target '${target}'`);
      }
    });

    const callDetails = target?.split(':')?.filter((x) => x);
    const parsed = this.parseCallee(callDetails?.[0]);
    if (!parsed) {
      this.loggingService.Error(`click-to-call protocol wrong format: ${msg?.data}`);
      this.errorService.showError(TranslationKeys.WarningIncorrectFormat, TranslationKeys.WarningContactYourAdministrator);
      return;
    }
    const { callee, type } = parsed;

    const teamAs = (callDetails.length > 1 ? callDetails[1] : null)?.toLowerCase();
    const team = this.services$.value?.find(
      (x) =>
        x.outboundCallState === OutboundCallState.Enabled &&
        (x.id.toLowerCase() === teamAs || x.serviceUpn.toLowerCase() === teamAs) &&
        (type !== IdentifierType.Pstn || x.pstnNumber),
    );
    if (!team) {
      this.openDialer(callee, msg?.data);
      return;
    }

    const outboundCallAttributes = { teamId: team.id, callerIdentifier: { id: callee, type } };
    this.loggingService.Info(`nimbus-call: ${JSON.stringify(outboundCallAttributes)}`);
    this.apiService.startOutboundCall(outboundCallAttributes).pipe(untilDestroyed(this)).subscribe();
  }

  /** `consult:<target>` - consults the target on a connected voice call, otherwise only pre-fills the dialer. */
  private processConsult(rawTarget: string, originalData: string): void {
    let parsed: { callee: string; type: IdentifierType } | null;
    try {
      parsed = this.parseCallee(rawTarget?.split(':')?.filter((x) => x)?.[0]);
    } catch {
      parsed = null;
    }
    if (!parsed) {
      this.loggingService.Error(`consult protocol wrong format: ${originalData}`);
      this.errorService.showError(TranslationKeys.WarningIncorrectFormat, TranslationKeys.WarningContactYourAdministrator);
      return;
    }
    const { callee, type } = parsed;
    this.loggingService.Info(`Received consult protocol with target '${callee}'`);

    const task = this.tasksService.connectedTaskSnapshot;
    if (task?.modality === Modality.Audio) {
      this.startConsultation(task, callee, type);
      return;
    }

    this.openDialer(callee, originalData);
  }

  /** Null when the callee is not a phone number, UPN or GUID. Throws on a malformed URI encoding. */
  private parseCallee(rawCallee: string | undefined): { callee: string; type: IdentifierType } | null {
    let callee = rawCallee ? decodeURIComponent(rawCallee) : '';

    const type = callee ? this.getCalleeType(callee) : undefined;
    if (type === undefined) {
      return null;
    }

    if (type === IdentifierType.Pstn) {
      ['-', ' ', '(', ')'].forEach((x) => (callee = Utilities.replaceAll(callee, x, '')));
    }
    return { callee, type };
  }

  private openDialer(callee: string, originalData: string): void {
    if (this.services$.value?.length) {
      this.clickToCallTarget$.next(callee);
      this.router.navigate([AppRoutes.outbound]);
      return;
    }

    this.loggingService.Error(`click-to-call no services: ${originalData}`);
    this.errorService.showError(TranslationKeys.WarningNoOutboundServices, TranslationKeys.WarningContactYourAdministrator);
  }

  /** Sends the consult dialer's request without opening it; an unresolved target opens it pre-filled instead. */
  private startConsultation(task: ITask, callee: string, type: IdentifierType): void {
    if (!this.canConsult(task)) {
      this.loggingService.Info(`consult ignored, call cannot be consulted in its current state: ${task.conversationState}`);
      return;
    }

    const transfer = new CallTransfer(
      task.unifiedConversationId,
      task.conversationId,
      task.modality,
      TransferType.consultativeTransfer,
      task.teamId,
      task.initialServiceName,
      task.caller?.displayName,
      !!task.teamInfo?.servicePstnNumber,
    );

    this.resolveConsultTarget(callee, type)
      .pipe(take(1), untilDestroyed(this))
      .subscribe((target) => {
        // The lookup is async: recheck the agent and the call.
        const current = this.tasksService.connectedTaskSnapshot;
        if (
          this.isInRona ||
          this.hasNoConnection ||
          current?.unifiedConversationId !== task.unifiedConversationId ||
          current?.conversationId !== task.conversationId ||
          !this.canConsult(current)
        ) {
          this.loggingService.Info(`consult dropped, call ${task.unifiedConversationId} is no longer consultable`);
          return;
        }

        if (!target) {
          this.loggingService.Info(`consult: target '${callee}' not resolved, opening the consultation dialer`);
          this.clickToCallTarget$.next(callee);
          this.transferService.processTransfer(transfer);
          return;
        }

        this.loggingService.Info(`consult: starting consultation on ${task.unifiedConversationId} to ${JSON.stringify(target)}`);
        transfer.transferTargetName = target.name;
        this.transferService.startConsultation(transfer, target).pipe(take(1), untilDestroyed(this)).subscribe();
      });
  }

  private isConsultOnConnectedCall(msg: UrlProtocolMessage): boolean {
    return (
      !!msg?.data?.toLowerCase()?.startsWith(`${TaskOutboundService.consultPrefix}:`) &&
      this.tasksService.connectedTaskSnapshot?.modality === Modality.Audio
    );
  }

  private canConsult(task: ITask): boolean {
    return (
      !isTransferDisabled(task) &&
      !TaskOutboundService.outboundBlockingStates.includes(task.conversationState) &&
      task.originType !== ConversationOriginType.TransferOnUserExit &&
      !isConsultationSessionInProgress(task.consultationSession?.currentState)
    );
  }

  /** Same value the consult dialer sends for the picked target. */
  private resolveConsultTarget(callee: string, type: IdentifierType): Observable<IAssistantUserAutoCompleteValueModel | null> {
    if (type === IdentifierType.Pstn) {
      return of({ value: callee, type: UserAutoCompleteValueType.PSTN });
    }

    if (type === IdentifierType.Guid) {
      // A service's resource account is a Graph user too: only real users are consulted directly.
      return this.graphApiService.getUserNames(callee).pipe(
        map((user) => {
          const upn = user?.userPrincipalName?.toLowerCase();
          const isService = (this.servicesService.serviceApplicationMappings$.value ?? []).some((x) => x.upn?.toLowerCase() === upn);
          return user?.displayName && !isService ? { value: callee, type: UserAutoCompleteValueType.GUID, name: user.displayName } : null;
        }),
        catchError(() => of(null)),
      );
    }

    return this.contactsService.search(callee, this.authService.getTenantId(), true).pipe(
      map((results) => {
        const option = (results ?? [])
          .flatMap((result) => result.callOptions ?? [])
          .find(
            (x) =>
              x.type === UserAutoCompleteCallOptionType.upn && (x.text?.toLowerCase() === callee || x.userId?.toLowerCase() === callee),
          );
        if (!option) {
          return null;
        }
        return {
          value: option.isService ? option.text : option.userId,
          type: UserAutoCompleteValueType.GUID,
          isService: option.isService,
          name: option.name,
        };
      }),
      catchError(() => of(null)),
    );
  }

  private getCalleeType(callee: string): IdentifierType {
    const guidRegexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
    if (guidRegexExp.test(callee)) {
      return IdentifierType.Guid;
    }

    const upnRegexExp = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+$/gi;
    if (upnRegexExp.test(callee)) {
      return IdentifierType.AcsUser;
    }

    const pstnRegexExp = /^[+]{0,1}[\s]*[(]{0,1}[\s]*[0-9]{1,4}[\s]*[)]{0,1}[-\s0-9]*$/gi;
    if (pstnRegexExp.test(callee)) {
      return IdentifierType.Pstn;
    }

    return undefined;
  }
}
